/**
 * The `@font-face` CSS that `html-to-image` inlines into the screenshot SVG.
 *
 * Left to itself, `html-to-image` builds this by walking every sheet in
 * `document.styleSheets` and reading its `cssRules`. The Google Maps JS API injects a
 * cross-origin `<link>` to fonts.googleapis.com without a `crossorigin` attribute, so
 * that sheet is not origin-clean and reading its rules throws a `SecurityError`. The
 * library swallows the error, but the `console.error` it logs is promoted to a Sentry
 * issue by `captureConsoleIntegration` — ~3500 events on ADMI-FRONTEND-1H.
 *
 * Passing a pre-built `fontEmbedCSS` skips that walk entirely: we fetch only the font
 * stylesheets we own and inline the font files as data URLs ourselves. Fonts have to be
 * inlined because the SVG is rasterised through an `<img>`, which cannot load external
 * resources.
 */

/**
 * The webfont stylesheets the app itself loads. Keep in sync with the `<link>` in
 * `index.html` and the `@import` in `src/features/sales/sales.css`.
 */
const FONT_STYLESHEET_URLS = [
    'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap',
    'https://fonts.googleapis.com/css?family=Hind:Light,Regular,Medium,SemiBold,Bold',
];

const FONT_URL_PATTERN = /url\(["']?(https:\/\/[^"')]+)["']?\)/g;

let cachedFontEmbedCss: Promise<string> | undefined;

async function fetchAsDataUrl(url: string): Promise<string> {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Could not fetch ${url}: ${response.status}`);
    }

    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${url} as a data URL`));
        reader.readAsDataURL(blob);
    });
}

async function inlineFontFiles(cssText: string): Promise<string> {
    const fontUrls = [...new Set([...cssText.matchAll(FONT_URL_PATTERN)].map(([, url]) => url))];

    const dataUrls = await Promise.all(fontUrls.map((url) => fetchAsDataUrl(url)));

    return fontUrls.reduce((css, url, index) => css.replaceAll(url, dataUrls[index]), cssText);
}

async function fetchEmbeddableStylesheet(url: string): Promise<string> {
    try {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Could not fetch ${url}: ${response.status}`);
        }

        return await inlineFontFiles(await response.text());
    } catch (error) {
        // A missing webfont only degrades the screenshot to a fallback font — never fail
        // the download over it. `warn` keeps this out of Sentry's console capture.
        console.warn(`Could not embed the webfonts from ${url}`, error);

        return '';
    }
}

/**
 * Builds the font CSS once per page load and reuses it for every later screenshot.
 */
export default function getFontEmbedCss(): Promise<string> {
    cachedFontEmbedCss ??= Promise.all(FONT_STYLESHEET_URLS.map(fetchEmbeddableStylesheet)).then((sheets) =>
        sheets.join('\n'),
    );

    return cachedFontEmbedCss;
}
